import type { LogEvent } from "@magic-md/engine-client/protocol";
import { frontendLog } from "../utils/frontend-logger";
import { useEditorStore } from "./editor-store";
import type { ExecutionProjectionStore } from "./execution-notification-projection";
import { useLogStore } from "./log-store";
import type { NotificationBus } from "./notification-bus";
import { useSearchStore } from "./search-store";

export function registerServiceNotificationHandlers(
  bus: NotificationBus,
  store: ExecutionProjectionStore,
): () => void {
  const disposers: Array<() => void> = [];
  const registerDomain = (
    prefix: string,
    handler: Parameters<NotificationBus["registerDomain"]>[1],
  ) => {
    disposers.push(bus.registerDomain(prefix, handler));
  };

  registerDomain("event/definition", () => {
    // Deprecated: execution.completed carries the same information.
  });
  registerDomain("event/sidecar", () => {
    // Sidecar panels use exact subscriptions.
  });

  registerDomain("event/log", (_method, params) => {
    const event = params as LogEvent;
    const sourceMap: Record<string, import("./log-store").LogSource> = {
      plugin: "plugin",
      sidecar: "sidecar",
      engine: "engine",
      execution: "engine",
      mcp: "mcp",
    };
    const mappedSource = (event.source && sourceMap[event.source]) || "engine";
    useLogStore.getState().log(event.level, event.message, {
      source: mappedSource,
      unitId: event.unitId,
      pluginName: event.pluginName,
      data: event.data,
    });
  });

  registerDomain("event/mcp", (method, params) => {
    if (method === "event/mcp.statusChanged") {
      const event =
        params as import("@magic-md/engine-client/protocol").McpStatusChangedEvent;
      frontendLog.debug(
        `[mcp] Status → ${event.status} (endpoint=${event.endpoint ?? "none"}, clients=${event.connectedClients ?? 0})`,
        undefined,
        { source: "mcp" },
      );
      useEditorStore
        .getState()
        .setMcpStatus(
          event.status,
          event.endpoint,
          event.port,
          event.connectedClients,
          event.clients,
          event.tools,
          event.prompts,
          event.resources,
          event.knownClients,
        );
    } else if (method === "event/mcp.requestLog") {
      const event =
        params as import("@magic-md/engine-client/protocol").McpRequestLogEntry;
      useEditorStore.getState().pushMcpRequestLog(event);
      if (event.status !== "pending") {
        const level = event.status === "error" ? "error" : "info";
        useLogStore.getState().log(level, event.name ?? event.method, {
          source: "mcp",
          data: {
            method: event.method,
            ...(event.arguments && { arguments: event.arguments }),
            ...(event.duration !== null && {
              duration: `${event.duration}ms`,
            }),
            ...(event.sessionId && { sessionId: event.sessionId }),
            ...(event.error && { error: event.error }),
          },
          ...(event.status === "error" && event.error
            ? { stack: event.error }
            : {}),
        });
      }
    }
  });

  registerDomain("event/gateway", (method, params) => {
    if (method !== "event/gateway.engineStatus") return;
    const event =
      params as import("@magic-md/engine-client/protocol").GatewayEngineStatusEvent;
    if (event.phase === "down") {
      useLogStore
        .getState()
        .warn(
          `Engine process exited (code=${event.code ?? "?"}, signal=${event.signal ?? "none"})`,
          { source: "engine" },
        );
      store.setState({ engineReady: false });
    } else if (event.phase === "restarting") {
      useLogStore
        .getState()
        .info(
          `Engine restarting in ${event.delayMs}ms (attempt ${event.attempt})…`,
          { source: "engine" },
        );
    } else if (event.phase === "up" && event.restarted) {
      useLogStore
        .getState()
        .info("Engine restarted — re-initializing session", {
          source: "engine",
        });
      store.setState({ engineReady: true, sessionId: null });
    }
  });

  registerDomain("event/stream", (method, params) => {
    if (method !== "event/stream.dropSummary") return;
    const event =
      params as import("@magic-md/engine-client/protocol").StreamDropSummaryEvent;
    useLogStore
      .getState()
      .warn(
        `Stream "${event.topic}" dropped ${event.dropped} chunks (${event.reason}, ${event.windowMs}ms window)`,
        { source: "engine", data: event },
      );
  });

  registerDomain("event/search", (method, params) => {
    const searchStore = useSearchStore.getState();
    switch (method) {
      case "event/search.results":
        searchStore._onSearchResults(
          params as import("@magic-md/engine-client/protocol").SearchResultsEvent,
        );
        break;
      case "event/search.complete":
        searchStore._onSearchComplete(
          params as import("@magic-md/engine-client/protocol").SearchCompleteEvent,
        );
        break;
      case "event/search.error":
        searchStore._onSearchError(
          params as import("@magic-md/engine-client/protocol").SearchErrorEvent,
        );
        break;
    }
  });

  return () => {
    for (const dispose of disposers.splice(0)) dispose();
  };
}
